import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
    Sheet, SheetContent, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import {
    Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import {
    Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import {
    Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { useToast } from '@/hooks/use-toast';
import {
    Users, Loader2, MapPin, Globe as GlobeIcon, Monitor,
    Plus, X, Send, ChevronRight, Calendar, Clock, Eye,
    MousePointerClick, PowerOff, MapPinned, Info, Tag,
    BarChart3, CheckCircle2, XCircle, Zap, Filter,
    ArrowUp, ArrowDown, ArrowUpDown, Search,
} from 'lucide-react';
import {
    getPushDomains, getPushLeads, getPushLeadStats,
    addPushLeadTags, removePushLeadTags, sendPushToLead,
    unsubscribeLead, getPushTags, createPushTag, getPushLead,
    getPushDomainDeliverability, getPushCampaignDeliveries,
    getPushLeadDeliveries,
    DeliverabilitySortField, DeliverabilitySortOrder,
} from '@/services/api/push';
import {
    PushLead, PushTag, PushPeriodFilter, PushDeliveryByCampaign, PushDelivery,
} from '@/types/push';

type MainTab = 'leads' | 'deliverability';
type DrawerTab = 'info' | 'push' | 'campaigns';

const PERIOD_OPTIONS: { value: 'all' | PushPeriodFilter; label: string }[] = [
    { value: 'all', label: 'Tudo' },
    { value: '1h', label: 'Última hora' },
    { value: '24h', label: 'Últimas 24h' },
    { value: '7d', label: 'Últimos 7 dias' },
    { value: '30d', label: 'Últimos 30 dias' },
];

type SortableTHProps = {
    field: DeliverabilitySortField;
    align: 'left' | 'right';
    current: DeliverabilitySortField | null;
    order: DeliverabilitySortOrder;
    onClick: (field: DeliverabilitySortField) => void;
    children: React.ReactNode;
};

function SortableTH({ field, align, current, order, onClick, children }: SortableTHProps) {
    const isActive = current === field;
    const Icon = !isActive ? ArrowUpDown : order === 'desc' ? ArrowDown : ArrowUp;
    return (
        <th
            scope="col"
            className={`px-5 py-3.5 text-${align} text-xs font-medium uppercase tracking-wider ${isActive ? 'text-gray-700' : 'text-gray-400'}`}
        >
            <button
                type="button"
                onClick={() => onClick(field)}
                className={`group inline-flex items-center gap-1.5 ${align === 'right' ? 'justify-end w-full' : ''} hover:text-gray-700 focus:outline-none focus:text-gray-700 transition-colors`}
            >
                {children}
                <Icon className={`h-3 w-3 ${isActive ? 'opacity-100' : 'opacity-30 group-hover:opacity-60'}`} />
            </button>
        </th>
    );
}

export default function Leads() {
    const [mainTab, setMainTab] = useState<MainTab>('leads');

    const [selectedDomainId, setSelectedDomainId] = useState<number | undefined>(undefined);
    const [statusFilter, setStatusFilter] = useState('');
    const [periodFilter, setPeriodFilter] = useState<PushPeriodFilter | ''>('');
    const [page, setPage] = useState(1);

    const [selectedLead, setSelectedLead] = useState<PushLead | null>(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerDefaultTab, setDrawerDefaultTab] = useState<DrawerTab>('info');

    const [tagPopoverOpen, setTagPopoverOpen] = useState(false);
    const [quickPush, setQuickPush] = useState({ title: '', body: '', url: '', icon: '', image: '' });

    const [isCreatingTag, setIsCreatingTag] = useState(false);
    const [newTagForm, setNewTagForm] = useState({ name: '', color: '#6B7280', description: '' });

    // Deliverability drill-down
    const [selectedCampaignRow, setSelectedCampaignRow] = useState<PushDeliveryByCampaign | null>(null);

    // Deliverability sort + filtros
    const [sortField, setSortField] = useState<DeliverabilitySortField | null>('total');
    const [sortOrder, setSortOrder] = useState<DeliverabilitySortOrder>('desc');
    const [kindFilter, setKindFilter] = useState<'all' | 'campaign' | 'flow'>('all');
    const [searchTerm, setSearchTerm] = useState('');

    // Filtros da aba "Campanhas Recebidas" no drawer
    const [drawerCampaignsFilter, setDrawerCampaignsFilter] = useState<{
        clicked: 'all' | 'yes' | 'no';
        success: 'all' | 'yes' | 'no';
    }>({ clicked: 'all', success: 'all' });

    const { toast } = useToast();
    const queryClient = useQueryClient();

    const { data: domains = [] } = useQuery({ queryKey: ['push-domains'], queryFn: getPushDomains });
    const configuredDomains = domains.filter((d) => d.push_config?.mongo_domain_id);

    const { data: stats, isLoading: statsLoading } = useQuery({
        queryKey: ['push-lead-stats', selectedDomainId],
        queryFn: () => getPushLeadStats(selectedDomainId),
    });

    const { data: leadsData, isLoading } = useQuery({
        queryKey: ['push-leads', selectedDomainId, statusFilter, periodFilter, page],
        queryFn: () => getPushLeads(selectedDomainId!, {
            page, limit: 20,
            status: statusFilter || undefined,
            period: periodFilter || undefined,
        }),
        enabled: !!selectedDomainId && mainTab === 'leads',
    });

    // Tags do domínio selecionado
    const { data: domainTags = [] } = useQuery<PushTag[]>({
        queryKey: ['push-tags', selectedDomainId],
        queryFn: () => getPushTags(selectedDomainId!),
        enabled: !!selectedDomainId,
    });

    // Entregabilidade agregada por campanha
    const { data: deliverability = [], isLoading: deliverabilityLoading } = useQuery<PushDeliveryByCampaign[]>({
        queryKey: ['push-deliverability', selectedDomainId, periodFilter, sortField, sortOrder, kindFilter, searchTerm],
        queryFn: () => getPushDomainDeliverability(selectedDomainId!, {
            period: periodFilter || undefined,
            sort_by: sortField || undefined,
            sort_order: sortField ? sortOrder : undefined,
            kind: kindFilter === 'all' ? undefined : kindFilter,
            search: searchTerm.trim() || undefined,
        }),
        enabled: !!selectedDomainId && mainTab === 'deliverability',
    });

    // Drill-down: leads que receberam a campanha selecionada
    const { data: campaignLeadsData, isLoading: campaignLeadsLoading } = useQuery({
        queryKey: ['push-campaign-deliveries', selectedCampaignRow?.campaignId, periodFilter],
        queryFn: () => getPushCampaignDeliveries(selectedCampaignRow!.campaignId, {
            limit: 50,
            period: periodFilter || undefined,
        }),
        enabled: !!selectedCampaignRow,
    });

    // Campanhas recebidas pelo lead selecionado (drawer tab)
    const { data: leadDeliveriesData, isLoading: leadDeliveriesLoading } = useQuery({
        queryKey: ['push-lead-deliveries', selectedLead?._id, drawerCampaignsFilter],
        queryFn: () => getPushLeadDeliveries(selectedLead!._id, {
            limit: 50,
            success: drawerCampaignsFilter.success === 'all' ? undefined : drawerCampaignsFilter.success === 'yes',
            clicked: drawerCampaignsFilter.clicked === 'all' ? undefined : drawerCampaignsFilter.clicked === 'yes',
        }),
        enabled: !!selectedLead && drawerOpen,
    });

    const addTagMutation = useMutation({
        mutationFn: ({ id, tags }: { id: string; tags: string[] }) => addPushLeadTags(id, tags),
        onSuccess: () => {
            toast({ title: 'Tag adicionada!' });
            queryClient.invalidateQueries({ queryKey: ['push-leads'] });
            if (selectedLead) refreshLead(selectedLead._id);
        },
    });

    const removeTagMutation = useMutation({
        mutationFn: ({ id, tags }: { id: string; tags: string[] }) => removePushLeadTags(id, tags),
        onSuccess: () => {
            toast({ title: 'Tag removida!' });
            queryClient.invalidateQueries({ queryKey: ['push-leads'] });
            if (selectedLead) refreshLead(selectedLead._id);
        },
    });

    const sendPushMutation = useMutation({
        mutationFn: ({ id, payload }: { id: string; payload: any }) => sendPushToLead(id, payload),
        onSuccess: () => {
            toast({ title: 'Push enviado com sucesso!' });
            setQuickPush({ title: '', body: '', url: '', icon: '', image: '' });
            if (selectedLead) refreshLead(selectedLead._id);
        },
        onError: (e: any) => toast({ variant: 'destructive', title: 'Erro ao enviar', description: e.message }),
    });

    const unsubscribeMutation = useMutation({
        mutationFn: unsubscribeLead,
        onSuccess: () => {
            toast({ title: 'Lead desativado' });
            queryClient.invalidateQueries({ queryKey: ['push-leads'] });
            queryClient.invalidateQueries({ queryKey: ['push-lead-stats'] });
            setDrawerOpen(false);
            setSelectedLead(null);
        },
        onError: (e: any) => toast({ variant: 'destructive', title: 'Erro', description: e.message }),
    });

    const createTagMutation = useMutation({
        mutationFn: (data: { domain_id: number; name: string; color: string; description?: string }) =>
            createPushTag(data),
        onSuccess: () => {
            toast({ title: 'Tag criada!' });
            queryClient.invalidateQueries({ queryKey: ['push-tags'] });
            if (selectedLead) {
                addPushLeadTags(selectedLead._id, [newTagForm.name.trim()]).then(() => {
                    refreshLead(selectedLead._id);
                    queryClient.invalidateQueries({ queryKey: ['push-leads'] });
                });
            }
            setIsCreatingTag(false);
            setNewTagForm({ name: '', color: '#6B7280', description: '' });
        },
        onError: (e: any) => toast({ variant: 'destructive', title: 'Erro', description: e.message }),
    });

    const leads = leadsData?.data || [];
    const pagination = leadsData?.pagination;

    const campaignLeads = campaignLeadsData?.data || [];
    const leadDeliveries = leadDeliveriesData?.data || [];

    const openDrawer = (lead: PushLead, tab: DrawerTab = 'info') => {
        setSelectedLead(lead);
        setDrawerDefaultTab(tab);
        setDrawerOpen(true);
    };

    // Abre drawer a partir de um delivery (após drill-down).
    const openDrawerFromDelivery = async (subscriptionId: string) => {
        try {
            const lead = await getPushLead(subscriptionId);
            openDrawer(lead, 'campaigns');
        } catch (e: any) {
            toast({ variant: 'destructive', title: 'Erro', description: 'Lead não encontrado' });
        }
    };

    const availableTagsForLead = domainTags.filter(
        (t) => !selectedLead?.tags?.includes(t.name)
    );

    const handleSelectTag = (tagName: string) => {
        if (!selectedLead) return;
        addTagMutation.mutate({ id: selectedLead._id, tags: [tagName] });
        setTagPopoverOpen(false);
    };

    const formatDate = (d: string | null) => {
        if (!d) return '-';
        return new Date(d).toLocaleString('pt-BR', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
        });
    };

    const formatDateShort = (d: string | null) => {
        if (!d) return '-';
        return new Date(d).toLocaleString('pt-BR', {
            day: '2-digit', month: '2-digit',
            hour: '2-digit', minute: '2-digit',
        });
    };

    const statusConfig: Record<string, { label: string; dot: string; bg: string }> = {
        active: { label: 'Ativo', dot: 'bg-emerald-500', bg: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
        unsubscribed: { label: 'Desinscrito', dot: 'bg-gray-400', bg: 'bg-gray-50 text-gray-600 border-gray-200' },
        failed: { label: 'Falhou', dot: 'bg-red-500', bg: 'bg-red-50 text-red-600 border-red-200' },
        expired: { label: 'Expirado', dot: 'bg-amber-500', bg: 'bg-amber-50 text-amber-600 border-amber-200' },
    };

    const getTagColor = (tagName: string): string => {
        const found = domainTags.find((t) => t.name === tagName);
        return found?.color || '#6B7280';
    };

    const refreshLead = async (leadId: string) => {
        try {
            const updated = await getPushLead(leadId);
            setSelectedLead(updated);
        } catch (e) {
            // silently fail
        }
    };

    // Numeric fields default to desc (highest first); name defaults to asc.
    const defaultOrderForField = (field: DeliverabilitySortField): DeliverabilitySortOrder =>
        field === 'campaign_name' ? 'asc' : 'desc';

    const toggleSort = (field: DeliverabilitySortField) => {
        if (sortField !== field) {
            setSortField(field);
            setSortOrder(defaultOrderForField(field));
            return;
        }
        setSortOrder((prev) => (prev === 'desc' ? 'asc' : 'desc'));
    };

    // Summary aggregado dos cards da aba Entregabilidade
    const deliverabilitySummary = deliverability.reduce(
        (acc, row) => ({
            total: acc.total + (row.total || 0),
            delivered: acc.delivered + (row.delivered || 0),
            failed: acc.failed + (row.failed || 0),
            clicked: acc.clicked + (row.clicked || 0),
        }),
        { total: 0, delivered: 0, failed: 0, clicked: 0 }
    );
    const overallCtr = deliverabilitySummary.delivered
        ? (deliverabilitySummary.clicked / deliverabilitySummary.delivered) * 100
        : 0;

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-3xl font-bold text-gray-900">Leads</h1>
                <p className="mt-1 text-gray-500">Gerencie seus assinantes de push notification</p>
            </div>

            {/* Stat Cards */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
                {[
                    { label: 'Total', value: stats?.total ?? 0, icon: Users, bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-900' },
                    { label: 'Ativos', value: stats?.active ?? 0, icon: Eye, bg: 'bg-emerald-50/70', border: 'border-emerald-200', text: 'text-emerald-700' },
                    { label: 'Desinscritos', value: stats?.unsubscribed ?? 0, icon: PowerOff, bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-500' },
                    { label: 'Falharam', value: stats?.failed ?? 0, icon: X, bg: 'bg-red-50/60', border: 'border-red-200', text: 'text-red-500' },
                    { label: 'Expirados', value: stats?.expired ?? 0, icon: Clock, bg: 'bg-amber-50/60', border: 'border-amber-200', text: 'text-amber-600' },
                ].map((s) => {
                    const Icon = s.icon;
                    return (
                        <div key={s.label} className={`rounded-2xl border ${s.border} ${s.bg} p-4 backdrop-blur-sm`}>
                            <div className="flex items-center justify-between">
                                <Icon className="h-4 w-4 text-gray-400" />
                                {statsLoading && <Loader2 className="h-3 w-3 animate-spin text-gray-300" />}
                            </div>
                            <p className={`mt-2 text-2xl font-bold ${s.text}`}>{s.value.toLocaleString()}</p>
                            <p className="mt-0.5 text-xs text-gray-400 uppercase tracking-wider">{s.label}</p>
                        </div>
                    );
                })}
            </div>

            {/* Main Tabs: Leads | Entregabilidade */}
            <Tabs value={mainTab} onValueChange={(v) => { setMainTab(v as MainTab); setPage(1); }}>
                <TabsList className="grid w-full max-w-md grid-cols-2 rounded-xl bg-gray-100 p-1">
                    <TabsTrigger value="leads" className="rounded-lg text-sm">
                        <Users className="mr-1.5 h-4 w-4" /> Leads
                    </TabsTrigger>
                    <TabsTrigger value="deliverability" className="rounded-lg text-sm">
                        <BarChart3 className="mr-1.5 h-4 w-4" /> Entregabilidade
                    </TabsTrigger>
                </TabsList>

                {/* ====== TAB: LEADS ====== */}
                <TabsContent value="leads" className="mt-6 space-y-4">
                    {/* Filters */}
                    <div className="flex flex-wrap items-center gap-3">
                        <Select
                            value={selectedDomainId?.toString() || 'all'}
                            onValueChange={(v) => { setSelectedDomainId(v === 'all' ? undefined : parseInt(v)); setPage(1); }}
                        >
                            <SelectTrigger className="w-56 rounded-xl border-gray-200 bg-white shadow-sm">
                                <SelectValue placeholder="Todos os domínios" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todos os domínios</SelectItem>
                                {configuredDomains.map((d) => (
                                    <SelectItem key={d.id} value={d.id.toString()}>{d.domain}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        {selectedDomainId && (
                            <>
                                <Select
                                    value={statusFilter || 'all'}
                                    onValueChange={(v) => { setStatusFilter(v === 'all' ? '' : v); setPage(1); }}
                                >
                                    <SelectTrigger className="w-44 rounded-xl border-gray-200 bg-white shadow-sm">
                                        <SelectValue placeholder="Status" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todos os status</SelectItem>
                                        <SelectItem value="active">Ativos</SelectItem>
                                        <SelectItem value="unsubscribed">Desinscritos</SelectItem>
                                        <SelectItem value="failed">Falharam</SelectItem>
                                        <SelectItem value="expired">Expirados</SelectItem>
                                    </SelectContent>
                                </Select>

                                <Select
                                    value={periodFilter || 'all'}
                                    onValueChange={(v) => { setPeriodFilter(v === 'all' ? '' : (v as PushPeriodFilter)); setPage(1); }}
                                >
                                    <SelectTrigger className="w-52 rounded-xl border-gray-200 bg-white shadow-sm">
                                        <Filter className="mr-1.5 h-3.5 w-3.5 text-gray-400" />
                                        <SelectValue placeholder="Período" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {PERIOD_OPTIONS.map((o) => (
                                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </>
                        )}
                    </div>

                    {/* No domain selected */}
                    {!selectedDomainId && (
                        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-white/60 p-16 backdrop-blur-sm">
                            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100">
                                <GlobeIcon className="h-7 w-7 text-gray-400" />
                            </div>
                            <h3 className="mt-5 text-base font-semibold text-gray-900">Selecione um domínio para ver leads</h3>
                            <p className="mt-1.5 max-w-sm text-center text-sm text-gray-400">
                                Escolha um domínio no filtro acima para visualizar e gerenciar os leads inscritos.
                            </p>
                        </div>
                    )}

                    {isLoading && selectedDomainId && (
                        <div className="flex items-center justify-center rounded-2xl border bg-white/60 p-16 backdrop-blur-sm">
                            <Loader2 className="h-7 w-7 animate-spin text-gray-400" />
                        </div>
                    )}

                    {/* Leads Table */}
                    {selectedDomainId && !isLoading && leads.length > 0 && (
                        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                            <table className="w-full text-sm">
                                <thead>
                                <tr className="border-b bg-gray-50/80">
                                    <th className="px-5 py-3.5 text-left text-xs font-medium uppercase tracking-wider text-gray-400">Lead</th>
                                    <th className="px-5 py-3.5 text-left text-xs font-medium uppercase tracking-wider text-gray-400">Status</th>
                                    <th className="px-5 py-3.5 text-left text-xs font-medium uppercase tracking-wider text-gray-400">Localização</th>
                                    <th className="px-5 py-3.5 text-left text-xs font-medium uppercase tracking-wider text-gray-400">Tags</th>
                                    <th className="px-5 py-3.5 text-left text-xs font-medium uppercase tracking-wider text-gray-400">Inscrito em</th>
                                    <th className="px-5 py-3.5 w-10"></th>
                                </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                {leads.map((lead) => {
                                    const st = statusConfig[lead.status] || statusConfig.active;
                                    return (
                                        <tr key={lead._id} className="cursor-pointer transition-colors hover:bg-gray-50/60" onClick={() => openDrawer(lead)}>
                                            <td className="px-5 py-3.5">
                                                <div>
                                                    <p className="font-medium text-gray-900">{lead.name || `#${lead._id.slice(-6)}`}</p>
                                                    {lead.email ? <p className="mt-0.5 text-xs text-gray-400">{lead.email}</p> : <p className="mt-0.5 text-xs text-gray-300">Sem contato</p>}
                                                </div>
                                            </td>
                                            <td className="px-5 py-3.5">
                                                    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${st.bg}`}>
                                                        <span className={`h-1.5 w-1.5 rounded-full ${st.dot}`} />
                                                        {st.label}
                                                    </span>
                                            </td>
                                            <td className="px-5 py-3.5">
                                                {lead.city || lead.country ? (
                                                    <span className="flex items-center gap-1.5 text-xs text-gray-500">
                                                            <MapPin className="h-3.5 w-3.5 text-gray-400" />
                                                        {[lead.city, lead.state, lead.countryCode].filter(Boolean).join(', ')}
                                                        </span>
                                                ) : <span className="text-xs text-gray-300">-</span>}
                                            </td>
                                            <td className="px-5 py-3.5">
                                                <div className="flex flex-wrap gap-1">
                                                    {lead.tags?.slice(0, 2).map((tag) => {
                                                        const color = getTagColor(tag);
                                                        return (
                                                            <Badge key={tag} variant="secondary" className="rounded-md text-xs font-normal border" style={{ backgroundColor: `${color}15`, color, borderColor: `${color}30` }}>
                                                                {tag}
                                                            </Badge>
                                                        );
                                                    })}
                                                    {(lead.tags?.length || 0) > 2 && <Badge variant="outline" className="rounded-md text-xs font-normal">+{lead.tags.length - 2}</Badge>}
                                                    {(!lead.tags || lead.tags.length === 0) && <span className="text-xs text-gray-300">-</span>}
                                                </div>
                                            </td>
                                            <td className="px-5 py-3.5 text-xs text-gray-400">{formatDate(lead.createdAt)}</td>
                                            <td className="px-5 py-3.5 text-right"><ChevronRight className="h-4 w-4 text-gray-300" /></td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {selectedDomainId && !isLoading && leads.length === 0 && (
                        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-white/60 p-16 backdrop-blur-sm">
                            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100"><Users className="h-7 w-7 text-gray-400" /></div>
                            <h3 className="mt-5 text-base font-semibold text-gray-900">Nenhum lead encontrado</h3>
                            <p className="mt-1.5 text-sm text-gray-400">Os leads aparecerão aqui quando visitantes aceitarem as notificações.</p>
                        </div>
                    )}

                    {pagination && pagination.totalPages > 1 && (
                        <div className="flex items-center justify-center gap-3">
                            <Button variant="outline" size="sm" className="rounded-xl" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
                            <span className="text-sm text-gray-400">{page} de {pagination.totalPages}</span>
                            <Button variant="outline" size="sm" className="rounded-xl" disabled={page >= pagination.totalPages} onClick={() => setPage(page + 1)}>Próxima</Button>
                        </div>
                    )}
                </TabsContent>

                {/* ====== TAB: ENTREGABILIDADE ====== */}
                <TabsContent value="deliverability" className="mt-6 space-y-4">
                    <div className="flex flex-wrap items-center gap-3">
                        <Select
                            value={selectedDomainId?.toString() || 'all'}
                            onValueChange={(v) => setSelectedDomainId(v === 'all' ? undefined : parseInt(v))}
                        >
                            <SelectTrigger className="w-56 rounded-xl border-gray-200 bg-white shadow-sm">
                                <SelectValue placeholder="Selecione um domínio" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Selecione um domínio</SelectItem>
                                {configuredDomains.map((d) => (
                                    <SelectItem key={d.id} value={d.id.toString()}>{d.domain}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        <Select
                            value={periodFilter || 'all'}
                            onValueChange={(v) => setPeriodFilter(v === 'all' ? '' : (v as PushPeriodFilter))}
                        >
                            <SelectTrigger className="w-52 rounded-xl border-gray-200 bg-white shadow-sm">
                                <Filter className="mr-1.5 h-3.5 w-3.5 text-gray-400" />
                                <SelectValue placeholder="Período" />
                            </SelectTrigger>
                            <SelectContent>
                                {PERIOD_OPTIONS.map((o) => (
                                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        <Select
                            value={kindFilter}
                            onValueChange={(v) => setKindFilter(v as 'all' | 'campaign' | 'flow')}
                        >
                            <SelectTrigger className="w-44 rounded-xl border-gray-200 bg-white shadow-sm">
                                <SelectValue placeholder="Tipo" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todos os tipos</SelectItem>
                                <SelectItem value="campaign">Campanhas</SelectItem>
                                <SelectItem value="flow">Flows</SelectItem>
                            </SelectContent>
                        </Select>

                        <div className="relative flex-1 min-w-[200px] max-w-xs">
                            <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                            <Input
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Buscar por nome..."
                                className="pl-9 rounded-xl border-gray-200 bg-white shadow-sm"
                            />
                        </div>
                    </div>

                    {!selectedDomainId && (
                        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-white/60 p-16 backdrop-blur-sm">
                            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100">
                                <BarChart3 className="h-7 w-7 text-gray-400" />
                            </div>
                            <h3 className="mt-5 text-base font-semibold text-gray-900">Selecione um domínio</h3>
                            <p className="mt-1.5 max-w-sm text-center text-sm text-gray-400">
                                A entregabilidade é agrupada por campanha. Escolha um domínio para ver os totais.
                            </p>
                        </div>
                    )}

                    {deliverabilityLoading && selectedDomainId && (
                        <div className="flex items-center justify-center rounded-2xl border bg-white/60 p-16 backdrop-blur-sm">
                            <Loader2 className="h-7 w-7 animate-spin text-gray-400" />
                        </div>
                    )}

                    {selectedDomainId && !deliverabilityLoading && deliverability.length > 0 && (
                        <>
                            {/* Summary cards */}
                            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                                    <div className="flex items-center justify-between">
                                        <Send className="h-4 w-4 text-gray-400" />
                                    </div>
                                    <p className="mt-2 text-2xl font-bold text-slate-900">{deliverabilitySummary.total.toLocaleString()}</p>
                                    <p className="mt-0.5 text-xs text-gray-400 uppercase tracking-wider">Enviados</p>
                                </div>
                                <div className="rounded-2xl border border-emerald-200 bg-emerald-50/70 p-4">
                                    <div className="flex items-center justify-between">
                                        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                                    </div>
                                    <p className="mt-2 text-2xl font-bold text-emerald-700">{deliverabilitySummary.delivered.toLocaleString()}</p>
                                    <p className="mt-0.5 text-xs text-emerald-500 uppercase tracking-wider">Entregues</p>
                                </div>
                                <div className="rounded-2xl border border-red-200 bg-red-50/60 p-4">
                                    <div className="flex items-center justify-between">
                                        <XCircle className="h-4 w-4 text-red-500" />
                                    </div>
                                    <p className="mt-2 text-2xl font-bold text-red-600">{deliverabilitySummary.failed.toLocaleString()}</p>
                                    <p className="mt-0.5 text-xs text-red-500 uppercase tracking-wider">Falhas</p>
                                </div>
                                <div className="rounded-2xl border border-blue-200 bg-blue-50/60 p-4">
                                    <div className="flex items-center justify-between">
                                        <MousePointerClick className="h-4 w-4 text-blue-500" />
                                        <Badge variant="outline" className="text-[10px] rounded-md border-blue-200 bg-white text-blue-600">
                                            CTR {overallCtr.toFixed(2)}%
                                        </Badge>
                                    </div>
                                    <p className="mt-2 text-2xl font-bold text-blue-700">{deliverabilitySummary.clicked.toLocaleString()}</p>
                                    <p className="mt-0.5 text-xs text-blue-500 uppercase tracking-wider">Cliques</p>
                                </div>
                            </div>

                            {/* Deliverability table */}
                            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                                <table className="w-full text-sm">
                                    <thead>
                                    <tr className="border-b bg-gray-50/80">
                                        <SortableTH field="campaign_name" align="left" current={sortField} order={sortOrder} onClick={toggleSort}>Campanha</SortableTH>
                                        <SortableTH field="total" align="right" current={sortField} order={sortOrder} onClick={toggleSort}>Enviados</SortableTH>
                                        <SortableTH field="delivered" align="right" current={sortField} order={sortOrder} onClick={toggleSort}>Entregues</SortableTH>
                                        <SortableTH field="failed" align="right" current={sortField} order={sortOrder} onClick={toggleSort}>Falhas</SortableTH>
                                        <SortableTH field="clicked" align="right" current={sortField} order={sortOrder} onClick={toggleSort}>Cliques</SortableTH>
                                        <SortableTH field="ctr" align="right" current={sortField} order={sortOrder} onClick={toggleSort}>CTR</SortableTH>
                                        <SortableTH field="lastSentAt" align="left" current={sortField} order={sortOrder} onClick={toggleSort}>Último envio</SortableTH>
                                        <th className="px-5 py-3.5 w-10"></th>
                                    </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                    {deliverability.map((row) => {
                                        const name = row.campaign_name
                                            || row.campaign_title
                                            || (row.kind === 'flow' ? `Flow node #${row.flow_node_id ?? ''}` : `Campanha ${row.campaignId}`);
                                        return (
                                            <tr
                                                key={row.campaignId}
                                                className="cursor-pointer transition-colors hover:bg-gray-50/60"
                                                onClick={() => setSelectedCampaignRow(row)}
                                            >
                                                <td className="px-5 py-3.5">
                                                    <div className="flex items-center gap-3">
                                                        {row.campaign_icon && (
                                                            <img src={row.campaign_icon} alt="" className="h-8 w-8 rounded-lg object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                                        )}
                                                        <div>
                                                            <p className="font-medium text-gray-900">{name}</p>
                                                            <div className="flex items-center gap-2 mt-0.5">
                                                                {row.kind === 'flow' ? (
                                                                    <Badge variant="outline" className="text-[10px] rounded-md border-purple-200 bg-purple-50 text-purple-600">
                                                                        <Zap className="h-2.5 w-2.5 mr-0.5" /> Flow
                                                                    </Badge>
                                                                ) : (
                                                                    <Badge variant="outline" className="text-[10px] rounded-md border-blue-200 bg-blue-50 text-blue-600">
                                                                        Campanha
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-5 py-3.5 text-right text-gray-900">{row.total.toLocaleString()}</td>
                                                <td className="px-5 py-3.5 text-right text-emerald-600">{row.delivered.toLocaleString()}</td>
                                                <td className="px-5 py-3.5 text-right text-red-500">{row.failed.toLocaleString()}</td>
                                                <td className="px-5 py-3.5 text-right text-blue-600">{row.clicked.toLocaleString()}</td>
                                                <td className="px-5 py-3.5 text-right">
                                                    <Badge variant="outline" className="rounded-md text-xs font-medium border-gray-200 bg-gray-50 text-gray-700">
                                                        {row.ctr.toFixed(2)}%
                                                    </Badge>
                                                </td>
                                                <td className="px-5 py-3.5 text-xs text-gray-400">{formatDateShort(row.lastSentAt)}</td>
                                                <td className="px-5 py-3.5 text-right"><ChevronRight className="h-4 w-4 text-gray-300" /></td>
                                            </tr>
                                        );
                                    })}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}

                    {selectedDomainId && !deliverabilityLoading && deliverability.length === 0 && (
                        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-white/60 p-16 backdrop-blur-sm">
                            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100"><BarChart3 className="h-7 w-7 text-gray-400" /></div>
                            <h3 className="mt-5 text-base font-semibold text-gray-900">Sem entregas no período</h3>
                            <p className="mt-1.5 text-sm text-gray-400">As estatísticas aparecerão aqui conforme as campanhas forem disparadas.</p>
                        </div>
                    )}
                </TabsContent>
            </Tabs>

            {/* === DRILL-DOWN SHEET: Leads de uma campanha === */}
            <Sheet open={!!selectedCampaignRow} onOpenChange={(o) => { if (!o) setSelectedCampaignRow(null); }}>
                <SheetContent className="w-full sm:max-w-2xl overflow-y-auto p-0">
                    {selectedCampaignRow && (
                        <div className="flex flex-col h-full">
                            <SheetHeader className="px-6 pt-6 pb-4 border-b">
                                <SheetTitle className="text-lg font-bold text-gray-900">
                                    {selectedCampaignRow.campaign_name
                                        || selectedCampaignRow.campaign_title
                                        || `Campanha ${selectedCampaignRow.campaignId}`}
                                </SheetTitle>
                                <p className="text-xs text-gray-400 mt-1">Leads que receberam esta campanha</p>
                            </SheetHeader>

                            <div className="grid grid-cols-4 gap-3 px-6 py-4 border-b bg-gray-50/40">
                                <div>
                                    <p className="text-[10px] font-medium uppercase tracking-wider text-gray-400">Enviados</p>
                                    <p className="mt-0.5 text-lg font-bold text-gray-900">{selectedCampaignRow.total.toLocaleString()}</p>
                                </div>
                                <div>
                                    <p className="text-[10px] font-medium uppercase tracking-wider text-emerald-500">Entregues</p>
                                    <p className="mt-0.5 text-lg font-bold text-emerald-700">{selectedCampaignRow.delivered.toLocaleString()}</p>
                                </div>
                                <div>
                                    <p className="text-[10px] font-medium uppercase tracking-wider text-red-500">Falhas</p>
                                    <p className="mt-0.5 text-lg font-bold text-red-600">{selectedCampaignRow.failed.toLocaleString()}</p>
                                </div>
                                <div>
                                    <p className="text-[10px] font-medium uppercase tracking-wider text-blue-500">Cliques</p>
                                    <p className="mt-0.5 text-lg font-bold text-blue-700">
                                        {selectedCampaignRow.clicked.toLocaleString()}
                                        <span className="ml-1 text-xs text-gray-400">({selectedCampaignRow.ctr.toFixed(2)}%)</span>
                                    </p>
                                </div>
                            </div>

                            <div className="flex-1 overflow-y-auto">
                                {campaignLeadsLoading ? (
                                    <div className="flex items-center justify-center p-12">
                                        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                                    </div>
                                ) : campaignLeads.length === 0 ? (
                                    <div className="p-12 text-center">
                                        <p className="text-sm text-gray-400">Nenhum lead encontrado</p>
                                    </div>
                                ) : (
                                    <table className="w-full text-sm">
                                        <thead>
                                        <tr className="border-b bg-gray-50/80">
                                            <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-400">Lead</th>
                                            <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-400">Status</th>
                                            <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-400">Enviado</th>
                                            <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-400">Clicou</th>
                                            <th className="px-5 py-3 w-10"></th>
                                        </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                        {campaignLeads.map((d: PushDelivery) => (
                                            <tr
                                                key={d._id}
                                                className="cursor-pointer transition-colors hover:bg-gray-50/60"
                                                onClick={() => {
                                                    openDrawerFromDelivery(d.subscriptionId);
                                                    setSelectedCampaignRow(null);
                                                }}
                                            >
                                                <td className="px-5 py-3">
                                                    <p className="font-medium text-gray-900">
                                                        {d.lead_name || `#${d.subscriptionId.slice(-6)}`}
                                                    </p>
                                                    {d.lead_email && (
                                                        <p className="text-xs text-gray-400">{d.lead_email}</p>
                                                    )}
                                                    {(d.lead_city || d.lead_country_code) && (
                                                        <p className="text-[11px] text-gray-400 mt-0.5">
                                                            {[d.lead_city, d.lead_country_code].filter(Boolean).join(', ')}
                                                        </p>
                                                    )}
                                                </td>
                                                <td className="px-5 py-3">
                                                    {d.success ? (
                                                        <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
                                                            <CheckCircle2 className="h-3.5 w-3.5" /> Entregue
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1 text-xs text-red-500">
                                                            <XCircle className="h-3.5 w-3.5" /> Falhou
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-5 py-3 text-xs text-gray-500">{formatDateShort(d.sentAt)}</td>
                                                <td className="px-5 py-3">
                                                    {d.clickedAt ? (
                                                        <span className="inline-flex items-center gap-1 text-xs text-blue-600">
                                                            <MousePointerClick className="h-3.5 w-3.5" /> {formatDateShort(d.clickedAt)}
                                                        </span>
                                                    ) : (
                                                        <span className="text-xs text-gray-300">-</span>
                                                    )}
                                                </td>
                                                <td className="px-5 py-3 text-right"><ChevronRight className="h-4 w-4 text-gray-300" /></td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                    )}
                </SheetContent>
            </Sheet>

            {/* === LEAD DETAIL DRAWER === */}
            <Sheet open={drawerOpen} onOpenChange={(open) => { setDrawerOpen(open); if (!open) setSelectedLead(null); }}>
                <SheetContent className="w-full sm:max-w-md overflow-y-auto p-0">
                    {selectedLead && (() => {
                        const st = statusConfig[selectedLead.status] || statusConfig.active;
                        return (
                            <div className="flex flex-col h-full">
                                <SheetHeader className="px-6 pt-6 pb-4 border-b">
                                    <SheetTitle className="text-lg font-bold text-gray-900">Detalhes do Lead</SheetTitle>
                                </SheetHeader>

                                <div className="flex items-center justify-between px-6 py-4">
                                    <div className="flex items-center gap-3">
                                        <span className="text-sm font-medium text-gray-500">#{selectedLead._id.slice(-4)}</span>
                                        <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${st.bg}`}>
                                            <span className={`h-1.5 w-1.5 rounded-full ${st.dot}`} />{st.label}
                                        </span>
                                    </div>
                                    {selectedLead.status === 'active' && (
                                        <button onClick={() => { if (confirm('Desativar este lead?')) unsubscribeMutation.mutate({ _id: selectedLead._id, endpoint: selectedLead.endpoint });
                                        }} className="text-sm font-medium text-red-500 hover:text-red-600 transition-colors">
                                            Desativar
                                        </button>
                                    )}
                                </div>

                                <Tabs defaultValue={drawerDefaultTab} className="flex-1">
                                    <TabsList className="mx-6 grid w-auto grid-cols-3 rounded-xl bg-gray-100 p-1">
                                        <TabsTrigger value="info" className="rounded-lg text-xs"><Info className="mr-1.5 h-3.5 w-3.5" /> Informações</TabsTrigger>
                                        <TabsTrigger value="push" className="rounded-lg text-xs"><Send className="mr-1.5 h-3.5 w-3.5" /> Enviar Push</TabsTrigger>
                                        <TabsTrigger value="campaigns" className="rounded-lg text-xs"><BarChart3 className="mr-1.5 h-3.5 w-3.5" /> Campanhas</TabsTrigger>
                                    </TabsList>

                                    {/* TAB INFO */}
                                    <TabsContent value="info" className="px-6 pb-6 space-y-4 mt-4">
                                        <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-4">
                                            {selectedLead.name || selectedLead.email || selectedLead.phone ? (
                                                <div className="space-y-1.5 text-sm">
                                                    {selectedLead.name && <p className="font-medium text-gray-900">{selectedLead.name}</p>}
                                                    {selectedLead.email && <p className="text-gray-500">{selectedLead.email}</p>}
                                                    {selectedLead.phone && <p className="text-gray-500">{selectedLead.phone}</p>}
                                                </div>
                                            ) : <p className="text-sm text-gray-400">Nenhum dado de contato</p>}
                                        </div>

                                        <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-4 space-y-3">
                                            {[
                                                { icon: GlobeIcon, label: 'PAÍS', value: selectedLead.country },
                                                { icon: MapPinned, label: 'ESTADO', value: selectedLead.state },
                                                { icon: MapPin, label: 'CIDADE', value: selectedLead.city },
                                                { icon: Monitor, label: 'IP', value: selectedLead.ip },
                                            ].map((item) => {
                                                const Icon = item.icon;
                                                return item.value ? (
                                                    <div key={item.label} className="flex items-start gap-3">
                                                        <Icon className="mt-0.5 h-4 w-4 text-gray-400" />
                                                        <div>
                                                            <p className="text-[10px] font-medium uppercase tracking-wider text-gray-400">{item.label}</p>
                                                            <p className="text-sm font-medium text-gray-900">{item.value}</p>
                                                        </div>
                                                    </div>
                                                ) : null;
                                            })}
                                            {!selectedLead.country && !selectedLead.city && !selectedLead.ip && <p className="text-sm text-gray-400">Sem dados de localização</p>}
                                        </div>

                                        <div className="grid grid-cols-2 gap-3">
                                            <div className="flex flex-col items-center rounded-xl border border-gray-100 bg-gray-50/50 p-4">
                                                <Eye className="h-5 w-5 text-gray-400" />
                                                <p className="mt-1.5 text-2xl font-bold text-gray-900">{selectedLead.totalReceived || 0}</p>
                                                <p className="text-[10px] font-medium uppercase tracking-wider text-gray-400">Recebidos</p>
                                            </div>
                                            <div className="flex flex-col items-center rounded-xl border border-emerald-100 bg-emerald-50/50 p-4">
                                                <MousePointerClick className="h-5 w-5 text-emerald-500" />
                                                <p className="mt-1.5 text-2xl font-bold text-emerald-700">{selectedLead.totalClicks || 0}</p>
                                                <p className="text-[10px] font-medium uppercase tracking-wider text-emerald-500">Cliques</p>
                                            </div>
                                        </div>

                                        <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-4 space-y-3">
                                            <div className="flex items-start gap-3">
                                                <Calendar className="mt-0.5 h-4 w-4 text-gray-400" />
                                                <div>
                                                    <p className="text-[10px] font-medium uppercase tracking-wider text-gray-400">Inscrito em</p>
                                                    <p className="text-sm font-medium text-gray-900">{formatDate(selectedLead.createdAt)}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-start gap-3">
                                                <Clock className="mt-0.5 h-4 w-4 text-gray-400" />
                                                <div>
                                                    <p className="text-[10px] font-medium uppercase tracking-wider text-gray-400">Última interação</p>
                                                    <p className="text-sm font-medium text-gray-900">{formatDate(selectedLead.lastPushAt || selectedLead.updatedAt)}</p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Tags — dropdown com tags do backend + criar nova */}
                                        <div>
                                            <div className="flex items-center justify-between mb-3">
                                                <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400">Tags</h4>
                                                <Popover open={tagPopoverOpen} onOpenChange={setTagPopoverOpen}>
                                                    <PopoverTrigger asChild>
                                                        <button className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors">
                                                            <Plus className="h-3.5 w-3.5" /> Adicionar
                                                        </button>
                                                    </PopoverTrigger>
                                                    <PopoverContent className="w-72 p-0" align="end" sideOffset={5}>
                                                        {!isCreatingTag ? (
                                                            <Command>
                                                                <CommandInput placeholder="Buscar tag..." />
                                                                <CommandList className="max-h-48">
                                                                    <CommandEmpty>
                                                                        <div className="px-4 py-4 text-center">
                                                                            <Tag className="mx-auto h-8 w-8 text-gray-300" />
                                                                            <p className="mt-2 text-sm text-gray-500">Nenhuma tag encontrada</p>
                                                                        </div>
                                                                    </CommandEmpty>
                                                                    <CommandGroup>
                                                                        {availableTagsForLead.map((tag) => (
                                                                            <CommandItem
                                                                                key={tag.id}
                                                                                value={tag.name}
                                                                                onSelect={() => handleSelectTag(tag.name)}
                                                                                className="flex items-center gap-2.5 px-3 py-2 cursor-pointer"
                                                                            >
                                                                                <div className="h-3 w-3 rounded-full shrink-0" style={{ backgroundColor: tag.color }} />
                                                                                <div className="flex-1 min-w-0">
                                                                                    <p className="text-sm font-medium text-gray-900 truncate">{tag.name}</p>
                                                                                    {tag.description && <p className="text-xs text-gray-400 truncate">{tag.description}</p>}
                                                                                </div>
                                                                            </CommandItem>
                                                                        ))}
                                                                    </CommandGroup>
                                                                </CommandList>
                                                                <div className="border-t p-2">
                                                                    <button
                                                                        onClick={() => setIsCreatingTag(true)}
                                                                        className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
                                                                    >
                                                                        <Plus className="h-4 w-4 text-gray-400" />
                                                                        Criar nova tag
                                                                    </button>
                                                                </div>
                                                            </Command>
                                                        ) : (
                                                            <div className="p-4 space-y-3">
                                                                <div className="flex items-center justify-between">
                                                                    <h4 className="text-sm font-semibold text-gray-900">Nova tag</h4>
                                                                    <button onClick={() => { setIsCreatingTag(false); setNewTagForm({ name: '', color: '#6B7280', description: '' }); }} className="text-gray-400 hover:text-gray-600">
                                                                        <X className="h-4 w-4" />
                                                                    </button>
                                                                </div>
                                                                <div>
                                                                    <Label className="text-xs text-gray-500">Nome *</Label>
                                                                    <Input
                                                                        value={newTagForm.name}
                                                                        onChange={(e) => setNewTagForm(p => ({ ...p, name: e.target.value }))}
                                                                        placeholder="ex: vip, promo_fev"
                                                                        className="mt-1 h-8 rounded-lg text-sm"
                                                                        autoFocus
                                                                    />
                                                                </div>
                                                                <div>
                                                                    <Label className="text-xs text-gray-500">Cor</Label>
                                                                    <div className="mt-1.5 flex gap-1.5">
                                                                        {['#6B7280', '#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899', '#06B6D4'].map((c) => (
                                                                            <button
                                                                                key={c}
                                                                                onClick={() => setNewTagForm(p => ({ ...p, color: c }))}
                                                                                className={`h-6 w-6 rounded-full transition-all ${newTagForm.color === c ? 'ring-2 ring-offset-1 ring-gray-400 scale-110' : 'hover:scale-105'}`}
                                                                                style={{ backgroundColor: c }}
                                                                            />
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                                <div>
                                                                    <Label className="text-xs text-gray-500">Descrição</Label>
                                                                    <Input
                                                                        value={newTagForm.description}
                                                                        onChange={(e) => setNewTagForm(p => ({ ...p, description: e.target.value }))}
                                                                        placeholder="Opcional"
                                                                        className="mt-1 h-8 rounded-lg text-sm"
                                                                    />
                                                                </div>
                                                                <Button
                                                                    size="sm"
                                                                    className="w-full rounded-lg bg-gray-900 hover:bg-gray-800 text-white"
                                                                    disabled={!newTagForm.name.trim() || createTagMutation.isPending}
                                                                    onClick={() => {
                                                                        if (!selectedDomainId || !newTagForm.name.trim()) return;
                                                                        createTagMutation.mutate({
                                                                            domain_id: selectedDomainId,
                                                                            name: newTagForm.name.trim(),
                                                                            color: newTagForm.color,
                                                                            description: newTagForm.description || undefined,
                                                                        });
                                                                    }}
                                                                >
                                                                    {createTagMutation.isPending ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Plus className="mr-2 h-3.5 w-3.5" />}
                                                                    Criar e adicionar
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </PopoverContent>
                                                </Popover>
                                            </div>
                                            <div className="flex flex-wrap gap-1.5">
                                                {selectedLead.tags?.map((tag) => {
                                                    const color = getTagColor(tag);
                                                    return (
                                                        <Badge key={tag} variant="secondary" className="gap-1.5 rounded-lg px-2.5 py-1 text-xs font-normal border" style={{ backgroundColor: `${color}12`, color, borderColor: `${color}30` }}>
                                                            <div className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
                                                            {tag}
                                                            <button onClick={(e) => { e.stopPropagation(); removeTagMutation.mutate({ id: selectedLead._id, tags: [tag] }); }} className="ml-0.5 opacity-60 hover:opacity-100 transition-opacity">
                                                                <X className="h-3 w-3" />
                                                            </button>
                                                        </Badge>
                                                    );
                                                })}
                                                {(!selectedLead.tags || selectedLead.tags.length === 0) && <p className="text-sm text-gray-400">Nenhuma tag atribuída</p>}
                                            </div>
                                        </div>

                                        {selectedLead.userAgent && (
                                            <div>
                                                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400">User Agent</h4>
                                                <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-3">
                                                    <p className="text-xs font-mono text-gray-500 break-all leading-relaxed">{selectedLead.userAgent}</p>
                                                </div>
                                            </div>
                                        )}

                                        <div>
                                            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400">Endpoint</h4>
                                            <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-3">
                                                <p className="text-xs font-mono text-gray-400 break-all leading-relaxed">{selectedLead.endpoint}</p>
                                            </div>
                                        </div>
                                    </TabsContent>

                                    {/* TAB PUSH */}
                                    <TabsContent value="push" className="px-6 pb-6 space-y-4 mt-4">
                                        <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-4">
                                            <div className="flex items-center gap-2 mb-1">
                                                <Send className="h-4 w-4 text-blue-500" />
                                                <p className="text-sm font-medium text-blue-900">Enviar push individual</p>
                                            </div>
                                            <p className="text-xs text-blue-600">Envie uma notificação diretamente para este lead.</p>
                                        </div>

                                        <div className="space-y-3">
                                            <div><Label className="text-xs text-gray-500">Título *</Label><Input value={quickPush.title} onChange={(e) => setQuickPush((p) => ({ ...p, title: e.target.value }))} placeholder="🔥 Oferta exclusiva!" className="mt-1 rounded-lg" maxLength={100} /></div>
                                            <div><Label className="text-xs text-gray-500">Mensagem *</Label><Input value={quickPush.body} onChange={(e) => setQuickPush((p) => ({ ...p, body: e.target.value }))} placeholder="Aproveite 50% de desconto..." className="mt-1 rounded-lg" maxLength={500} /></div>
                                            <div><Label className="text-xs text-gray-500">URL de destino *</Label><Input value={quickPush.url} onChange={(e) => setQuickPush((p) => ({ ...p, url: e.target.value }))} placeholder="https://seusite.com/promo" className="mt-1 rounded-lg" /></div>
                                            <div><Label className="text-xs text-gray-500">Ícone (URL)</Label><Input value={quickPush.icon} onChange={(e) => setQuickPush((p) => ({ ...p, icon: e.target.value }))} placeholder="https://seusite.com/icon.png" className="mt-1 rounded-lg" /></div>
                                            <div><Label className="text-xs text-gray-500">Imagem (URL)</Label><Input value={quickPush.image} onChange={(e) => setQuickPush((p) => ({ ...p, image: e.target.value }))} placeholder="https://seusite.com/banner.jpg" className="mt-1 rounded-lg" /></div>
                                        </div>

                                        {quickPush.title && (
                                            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                                                <p className="text-[10px] font-medium uppercase tracking-wider text-gray-400 mb-2">Preview</p>
                                                <div className="flex gap-3">
                                                    {quickPush.icon && <img src={quickPush.icon} alt="icon" className="h-10 w-10 rounded-lg object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />}
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-semibold text-gray-900 truncate">{quickPush.title}</p>
                                                        <p className="text-xs text-gray-500 truncate">{quickPush.body}</p>
                                                        {quickPush.url && <p className="mt-0.5 text-[10px] text-blue-500 truncate">{quickPush.url}</p>}
                                                    </div>
                                                </div>
                                                {quickPush.image && <img src={quickPush.image} alt="banner" className="mt-2 w-full rounded-lg object-cover max-h-32" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />}
                                            </div>
                                        )}

                                        <Button
                                            className="w-full rounded-xl text-white bg-gray-900 hover:bg-gray-800"
                                            disabled={!quickPush.title || !quickPush.body || !quickPush.url || sendPushMutation.isPending}
                                            onClick={() => sendPushMutation.mutate({
                                                id: selectedLead._id,
                                                payload: { title: quickPush.title, body: quickPush.body, url: quickPush.url, ...(quickPush.icon && { icon: quickPush.icon }), ...(quickPush.image && { image: quickPush.image }) },
                                            })}
                                        >
                                            {sendPushMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4 text-white" />}
                                            Enviar Push
                                        </Button>
                                    </TabsContent>

                                    {/* TAB CAMPANHAS RECEBIDAS */}
                                    <TabsContent value="campaigns" className="px-6 pb-6 space-y-4 mt-4">
                                        <div className="flex items-center gap-2">
                                            <Select
                                                value={drawerCampaignsFilter.success}
                                                onValueChange={(v) => setDrawerCampaignsFilter((p) => ({ ...p, success: v as any }))}
                                            >
                                                <SelectTrigger className="h-8 rounded-lg text-xs flex-1">
                                                    <SelectValue placeholder="Status" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="all">Todos</SelectItem>
                                                    <SelectItem value="yes">Entregues</SelectItem>
                                                    <SelectItem value="no">Falhas</SelectItem>
                                                </SelectContent>
                                            </Select>
                                            <Select
                                                value={drawerCampaignsFilter.clicked}
                                                onValueChange={(v) => setDrawerCampaignsFilter((p) => ({ ...p, clicked: v as any }))}
                                            >
                                                <SelectTrigger className="h-8 rounded-lg text-xs flex-1">
                                                    <SelectValue placeholder="Clique" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="all">Todos</SelectItem>
                                                    <SelectItem value="yes">Com clique</SelectItem>
                                                    <SelectItem value="no">Sem clique</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        {leadDeliveriesLoading ? (
                                            <div className="flex items-center justify-center p-8">
                                                <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                                            </div>
                                        ) : leadDeliveries.length === 0 ? (
                                            <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/40 p-10 text-center">
                                                <BarChart3 className="mx-auto h-7 w-7 text-gray-300" />
                                                <p className="mt-3 text-sm text-gray-400">Nenhuma entrega registrada</p>
                                            </div>
                                        ) : (
                                            <div className="space-y-2">
                                                {leadDeliveries.map((d: PushDelivery) => {
                                                    const label = d.campaign_name
                                                        || d.campaign_title
                                                        || (d.kind === 'flow' ? `Flow node #${d.flow_node_id ?? ''}` : `Campanha ${d.campaignId}`);
                                                    return (
                                                        <div
                                                            key={d._id}
                                                            className="flex items-start gap-3 rounded-xl border border-gray-100 bg-white p-3 shadow-sm"
                                                        >
                                                            {d.campaign_icon ? (
                                                                <img
                                                                    src={d.campaign_icon}
                                                                    alt=""
                                                                    className="h-9 w-9 rounded-lg object-cover"
                                                                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                                                />
                                                            ) : (
                                                                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-100">
                                                                    {d.kind === 'flow' ? (
                                                                        <Zap className="h-4 w-4 text-purple-500" />
                                                                    ) : (
                                                                        <Send className="h-4 w-4 text-gray-400" />
                                                                    )}
                                                                </div>
                                                            )}
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-sm font-medium text-gray-900 truncate">{label}</p>
                                                                <p className="text-[11px] text-gray-400 mt-0.5">{formatDate(d.sentAt)}</p>
                                                                <div className="flex items-center gap-2 mt-1.5">
                                                                    {d.success ? (
                                                                        <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600">
                                                                            <CheckCircle2 className="h-3 w-3" /> Entregue
                                                                        </span>
                                                                    ) : (
                                                                        <span className="inline-flex items-center gap-1 text-[11px] text-red-500">
                                                                            <XCircle className="h-3 w-3" /> Falhou
                                                                        </span>
                                                                    )}
                                                                    {d.clickedAt && (
                                                                        <span className="inline-flex items-center gap-1 text-[11px] text-blue-600">
                                                                            <MousePointerClick className="h-3 w-3" /> Clicou
                                                                        </span>
                                                                    )}
                                                                    {d.kind === 'flow' && (
                                                                        <Badge variant="outline" className="text-[9px] px-1.5 py-0 rounded-md border-purple-200 bg-purple-50 text-purple-600">
                                                                            Flow
                                                                        </Badge>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </TabsContent>
                                </Tabs>
                            </div>
                        );
                    })()}
                </SheetContent>
            </Sheet>
        </div>
    );
}
